import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Pagination
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Assessment as AssessmentIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PasswordResetLinkDialog from '../components/PasswordResetLinkDialog';
import { buildPasswordResetLink } from '../utils/publicFrontendUrl';
import SortableTableCell, { toggleSort, SortDirection } from '../components/SortableTableCell';

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  position: string;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  role: string;
  accountType?: 'corporate' | 'invited';
  company?: {
    _id: string;
    name: string;
  } | null;
  riskLevel: string;
  progress: any[];
  evaluations: any[];
  isLocked?: boolean;
  lockedUntil?: string | null;
  loginAttempts?: number;
}

interface EmployeeDetail extends Employee {
  progress: any[];
  evaluations: any[];
}

const AdminEmployees: React.FC = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lastAccessFilter, setLastAccessFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  
  // Estados para el diálogo de detalles
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeDetail | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Estados para el diálogo de cambio de rol
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<Employee | null>(null);
  const [newRole, setNewRole] = useState('');
  const [roleLoading, setRoleLoading] = useState(false);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Diálogo de enlace de recuperación de contraseña
  const [resetLinkDialogOpen, setResetLinkDialogOpen] = useState(false);
  const [resetLinkData, setResetLinkData] = useState<{
    employeeId: string;
    employeeName: string;
    employeeEmail: string;
    resetLink: string;
    resetToken: string;
  } | null>(null);

  // Lista de departamentos únicos
  const [departments, setDepartments] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Array<{_id: string, name: string}>>([]);

  // Cargar todas las empresas al montar el componente
  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [currentPage, searchTerm, departmentFilter, companyFilter, statusFilter, lastAccessFilter, accountTypeFilter, roleFilter, orderBy, order]);

  const loadCompanies = async () => {
    try {
      // Cargar todas las empresas disponibles para el combo
      // Usamos un límite alto para obtener todas las empresas
      const response = await api.getCompanies({ limit: 10000, page: 1 });
      const companiesList = response.companies.map((company: any) => ({
        _id: company._id,
        name: company.name
      }));
      // Ordenar empresas alfabéticamente por nombre
      companiesList.sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(companiesList);
    } catch (err: any) {
      console.error('Error cargando empresas:', err);
      // No mostrar error al usuario ya que no es crítico para la funcionalidad principal
      setCompanies([]);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const useClientRoleFilter = roleFilter !== 'all';
      const response = await api.getAdminEmployees({
        page: useClientRoleFilter ? 1 : currentPage,
        limit: useClientRoleFilter ? 5000 : 20,
        search: searchTerm,
        department: departmentFilter,
        companyId: companyFilter,
        status: statusFilter === 'all' ? undefined : statusFilter === 'active' ? 'true' : 'false',
        lastAccess: lastAccessFilter !== 'all' ? lastAccessFilter : undefined,
        accountType: accountTypeFilter !== 'all' ? accountTypeFilter : undefined,
        userRole: useClientRoleFilter ? roleFilter : undefined,
        role: useClientRoleFilter ? roleFilter : undefined,
        sortBy: orderBy,
        sortOrder: order
      });

      let list = response.employees || [];
      if (useClientRoleFilter) {
        list = list.filter((emp: Employee) => emp.role === roleFilter);
      }

      setEmployees(list);
      if (useClientRoleFilter) {
        setTotalPages(1);
        setTotalEmployees(list.length);
      } else {
        setTotalPages(response.pagination.total);
        setTotalEmployees(response.pagination.totalRecords);
      }
      
      // Extraer departamentos únicos de los empleados filtrados
      const departments: string[] = [];
      list.forEach((emp: Employee) => {
        if (emp.department && !departments.includes(emp.department)) {
          departments.push(emp.department);
        }
      });
      setDepartments(departments);
      
      // No extraer empresas de los empleados, ya se cargan todas por separado
      
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando empleados',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmployee = async (employeeId: string) => {
    try {
      setDetailLoading(true);
      const response = await api.getAdminEmployeeDetail(employeeId);
      // El backend retorna { user: userDetail }
      const employeeDetail = {
        ...response.user,
        progress: response.user?.progress || [],
        evaluations: response.user?.evaluations || []
      };
      setSelectedEmployee(employeeDetail);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando detalles del empleado',
        severity: 'error'
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleUserStatus = async (employeeId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      const action = newStatus ? 'activar' : 'desactivar';
      
      // Confirmar acción
      if (!window.confirm(`¿Estás seguro de que quieres ${action} este usuario?`)) {
        return;
      }

      await api.toggleUserStatus(employeeId, newStatus);
      
      // Actualizar la lista de empleados
      await loadEmployees();
      
      setSnackbar({
        open: true,
        message: `Usuario ${newStatus ? 'activado' : 'desactivado'} exitosamente`,
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error al cambiar el estado del usuario',
        severity: 'error'
      });
    }
  };

  const handleUnlockUser = async (employeeId: string, employeeEmail: string) => {
    try {
      // Confirmar acción
      if (!window.confirm(`¿Estás seguro de que quieres desbloquear la cuenta de ${employeeEmail}?`)) {
        return;
      }

      await api.unlockUser(employeeId);
      
      // Actualizar la lista de empleados
      await loadEmployees();
      
      setSnackbar({
        open: true,
        message: 'Cuenta desbloqueada exitosamente',
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error al desbloquear la cuenta',
        severity: 'error'
      });
    }
  };

  const [resetLinkLoadingId, setResetLinkLoadingId] = useState<string | null>(null);

  const handleSendPasswordReset = async (employee: Employee) => {
    try {
      setResetLinkLoadingId(employee._id);
      // Solo genera el enlace; el correo se envía desde el modal (copiar / enviar)
      const response = await api.generateEmployeePasswordResetLink(employee._id, {
        sendEmail: false
      });

      if (response.resetToken) {
        const resetLink = buildPasswordResetLink(response.resetToken);
        setResetLinkData({
          employeeId: employee._id,
          employeeName: `${employee.firstName} ${employee.lastName}`,
          employeeEmail: employee.email,
          resetLink,
          resetToken: response.resetToken
        });
        setResetLinkDialogOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: 'No se pudo generar el enlace de recuperación',
          severity: 'error'
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error generando enlace de recuperación',
        severity: 'error'
      });
    } finally {
      setResetLinkLoadingId(null);
    }
  };

  const handleSendResetEmailFromModal = async () => {
    if (!resetLinkData) return;
    await api.sendEmployeePasswordResetEmail(
      resetLinkData.employeeId,
      resetLinkData.resetToken
    );
  };

  const handleChangeRole = (employee: Employee) => {
    setSelectedUserForRole(employee);
    setNewRole(employee.role);
    setRoleDialogOpen(true);
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setSelectedUserForRole(null);
    setNewRole('');
  };

  const handleConfirmRoleChange = async () => {
    if (!selectedUserForRole || !newRole) return;

    setRoleLoading(true);
    try {
      await api.updateUserRole(selectedUserForRole._id, newRole);
      
      setSnackbar({
        open: true,
        message: `Rol de ${selectedUserForRole.firstName} ${selectedUserForRole.lastName} cambiado exitosamente a ${getRoleLabel(newRole)}`,
        severity: 'success'
      });
      
      handleCloseRoleDialog();
      loadEmployees();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cambiando rol del usuario',
        severity: 'error'
      });
    } finally {
      setRoleLoading(false);
    }
  };

  const getStatusLabel = (isActive: boolean) => isActive ? 'Activo' : 'Inactivo';
  const getStatusColor = (isActive: boolean) => isActive ? 'success' : 'error';
  
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Super Admin';
      case 'company_admin': return 'Admin de Empresa';
      case 'employee': return 'Empleado';
      default: return role;
    }
  };
  
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'company_admin': return 'warning';
      case 'employee': return 'default';
      default: return 'default';
    }
  };

  const getAccountTypeLabel = (accountType?: string) =>
    accountType === 'invited' ? 'Invitado (personal)' : 'Corporativo';

  const getAccountTypeColor = (accountType?: string): 'primary' | 'secondary' =>
    accountType === 'invited' ? 'secondary' : 'primary';
  
  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getRiskLevelText = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low': return 'Bajo';
      case 'medium': return 'Medio';
      case 'high': return 'Alto';
      case 'critical': return 'Crítico';
      default: return 'Desconocido';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedEmployee(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRequestSort = (sortKey: string) => {
    const next = toggleSort(orderBy, order, sortKey);
    setOrderBy(next.orderBy);
    setOrder(next.order);
    setCurrentPage(1);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <PeopleIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Gestión de Empleados
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administra todos los empleados del sistema
        </Typography>
      </Box>

      {/* Filtros y búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(7, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'end'
          }}>
            <TextField
              label="Buscar empleados"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={companyFilter}
                onChange={(e) => { setCompanyFilter(e.target.value); setCurrentPage(1); }}
                label="Empresa"
              >
                <MenuItem value="">Todas las empresas</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company._id} value={company._id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Departamento</InputLabel>
              <Select
                value={departmentFilter}
                onChange={(e) => { setDepartmentFilter(e.target.value); setCurrentPage(1); }}
                label="Departamento"
              >
                <MenuItem value="">Todos los departamentos</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                value={accountTypeFilter}
                onChange={(e) => { setAccountTypeFilter(e.target.value); setCurrentPage(1); }}
                label="Tipo"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="corporate">Corporativo</MenuItem>
                <MenuItem value="invited">Invitado (personal)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Rol</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                label="Rol"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="employee">Empleado</MenuItem>
                <MenuItem value="company_admin">Admin de empresa</MenuItem>
                <MenuItem value="admin">Super Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                label="Estado"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="active">Activos</MenuItem>
                <MenuItem value="inactive">Inactivos</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Último acceso</InputLabel>
              <Select
                value={lastAccessFilter}
                onChange={(e) => { setLastAccessFilter(e.target.value); setCurrentPage(1); }}
                label="Último acceso"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="never">Sin ingreso a la plataforma</MenuItem>
                <MenuItem value="logged">Ya ingresaron</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total: {totalEmployees} empleados
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Tabla de empleados */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableCell label="Empleado" sortKey="name" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <SortableTableCell label="Empresa" sortKey="company" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <SortableTableCell label="Departamento" sortKey="department" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <SortableTableCell label="Tipo" sortKey="type" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <SortableTableCell label="Rol" sortKey="role" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <SortableTableCell label="Estado" sortKey="status" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <TableCell>Nivel de Riesgo</TableCell>
                  <SortableTableCell label="Último Acceso" sortKey="lastLogin" orderBy={orderBy} order={order} onRequestSort={handleRequestSort} />
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Cargando empleados...
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No se encontraron empleados
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {employee.firstName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {employee.firstName} {employee.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {employee.company?.name || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.department || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getAccountTypeLabel(employee.accountType)}
                          color={getAccountTypeColor(employee.accountType)}
                          size="small"
                          variant={employee.accountType === 'invited' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(employee.role)}
                          color={getRoleColor(employee.role) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={getStatusLabel(employee.isActive)}
                            color={getStatusColor(employee.isActive)}
                            size="small"
                          />
                          {employee.isLocked && (
                            <Chip
                              icon={<LockIcon />}
                              label="Bloqueado"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getRiskLevelText(employee.riskLevel)}
                          color={getRiskLevelColor(employee.riskLevel) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(employee.lastLogin)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Ver detalles">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewEmployee(employee._id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restablecer contraseña">
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                disabled={resetLinkLoadingId === employee._id}
                                onClick={() => handleSendPasswordReset(employee)}
                              >
                                <LockResetIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {employee.isLocked && (
                            <Tooltip title="Desbloquear cuenta">
                              <IconButton 
                                size="small" 
                                color="warning"
                                onClick={() => handleUnlockUser(employee._id, employee.email)}
                              >
                                <LockOpenIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={employee.isActive ? "Desactivar usuario" : "Activar usuario"}>
                            <IconButton 
                              size="small" 
                              color={employee.isActive ? "error" : "success"}
                              onClick={() => handleToggleUserStatus(employee._id, employee.isActive)}
                            >
                              {employee.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cambiar rol">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleChangeRole(employee)}
                            >
                              {employee.role === 'admin' ? <AdminIcon /> : <PersonAddIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de detalles del empleado */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PersonIcon />
            <Typography variant="h6">
              {selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : 'Detalles del Empleado'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Cargando detalles...</Typography>
            </Box>
          ) : selectedEmployee ? (
            <Box>
              {/* Información básica */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
                mb: 3
              }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Información Personal
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedEmployee?.email || 'N/A'}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Empresa
                    </Typography>
                    <Typography variant="body1">{selectedEmployee.company?.name || 'N/A'}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Departamento
                    </Typography>
                    <Typography variant="body1">{selectedEmployee?.department || 'N/A'}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Cargo
                    </Typography>
                    <Typography variant="body1">{selectedEmployee?.position || 'N/A'}</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Estado de la Cuenta
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Estado
                    </Typography>
                    <Chip
                      label={getStatusLabel(selectedEmployee?.isActive ?? false)}
                      color={getStatusColor(selectedEmployee?.isActive ?? false)}
                      size="small"
                    />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Último Acceso
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedEmployee?.lastLogin)}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Fecha de Registro
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedEmployee?.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Progreso de cursos */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Progreso de Cursos
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Curso</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Progreso</TableCell>
                        <TableCell>Puntuación</TableCell>
                        <TableCell>Última Actualización</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedEmployee?.progress?.length || 0) === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No hay progreso registrado
                          </TableCell>
                        </TableRow>
                      ) : (
                        (selectedEmployee?.progress || []).map((progress) => (
                          <TableRow key={progress._id}>
                            <TableCell>
                              <Typography variant="body2">
                                {progress.course?.title || 'Curso no encontrado'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={progress.status}
                                color={progress.status === 'completed' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {progress.progress}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {progress.score || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(progress.updatedAt)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Evaluaciones completadas */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Evaluaciones Completadas
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Curso</TableCell>
                        <TableCell>Puntuación</TableCell>
                        <TableCell>Fecha de Completado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedEmployee?.evaluations?.length || 0) === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            No hay evaluaciones completadas
                          </TableCell>
                        </TableRow>
                      ) : (
                        (selectedEmployee?.evaluations || []).map((evaluation) => (
                          <TableRow key={evaluation._id}>
                            <TableCell>
                              <Typography variant="body2">
                                {evaluation.course?.title || 'Curso no encontrado'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {evaluation.score}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(evaluation.completedAt)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {selectedEmployee && (
            <Tooltip title="Restablecer contraseña">
              <span>
                <IconButton
                  color="warning"
                  disabled={resetLinkLoadingId === selectedEmployee._id}
                  onClick={() => handleSendPasswordReset(selectedEmployee)}
                >
                  <LockResetIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Button onClick={handleCloseDetailDialog}>
            CERRAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para cambiar rol */}
      <Dialog open={roleDialogOpen} onClose={handleCloseRoleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Cambiar Rol de Usuario
        </DialogTitle>
        <DialogContent>
          {selectedUserForRole && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Usuario: <strong>{selectedUserForRole.firstName} {selectedUserForRole.lastName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Email: {selectedUserForRole.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Empresa: {selectedUserForRole.company?.name || 'Sin empresa'}
              </Typography>
              
              <FormControl fullWidth>
                <InputLabel>Nuevo Rol</InputLabel>
                <Select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  label="Nuevo Rol"
                >
                  <MenuItem value="employee">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      <Box>
                        <Typography variant="body2">Empleado</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Acceso básico a cursos y evaluaciones
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="company_admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon />
                      <Box>
                        <Typography variant="body2">Admin de Empresa</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Gestión de empleados y reportes de la empresa
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AdminIcon />
                      <Box>
                        <Typography variant="body2">Super Admin</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Acceso completo al sistema
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              
              {newRole !== selectedUserForRole.role && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Advertencia:</strong> Cambiar el rol de un usuario puede afectar sus permisos de acceso.
                    El usuario será notificado del cambio.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleDialog} disabled={roleLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmRoleChange} 
            variant="contained" 
            disabled={roleLoading || !newRole || newRole === selectedUserForRole?.role}
          >
            {roleLoading ? 'Cambiando...' : 'Cambiar Rol'}
          </Button>
        </DialogActions>
      </Dialog>

      {resetLinkData && (
        <PasswordResetLinkDialog
          open={resetLinkDialogOpen}
          onClose={() => {
            setResetLinkDialogOpen(false);
            setResetLinkData(null);
          }}
          employeeName={resetLinkData.employeeName}
          employeeEmail={resetLinkData.employeeEmail}
          resetLink={resetLinkData.resetLink}
          onSendEmail={handleSendResetEmailFromModal}
        />
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminEmployees;